import { EnhancedCard } from 'components/enhanced/card';
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'shared/utils/toast';
import { IInvoice } from '../../types';
import InvoiceAddDDTDialog from '../dialog/InvoiceAddDDTDialog';
import { transportDocumentService } from 'features/inventory/transport-documents/TransportDocument/api/transport-document.service';
import { AddButton } from 'components/ui';
import EnhancedDataGrid from 'components/enhanced/data-grid/EnhancedDataGrid';
import { TransportDocumentColumns } from 'features/inventory/transport-documents/TransportDocument/transport-document';

const TRANSLATION_PREFIX = 'pages.invoices.update';

const FIELDS_TO_FILTER_OUT = [
  'billable',
  'closed',
  'notes',
  'invoice_ref',
  'invoice_date',
  'amount'
];

interface InvoceDDTCardProps {
  invoice: IInvoice;
  dataGridRefreshRef: React.RefObject<(() => void) | null>;
  refreshInvoice: () => void;
  setIsAlertErrorColorVisible: (visible: boolean) => void;
}

export const InvoceDDTCard = ({
  invoice,
  dataGridRefreshRef,
  refreshInvoice,
  setIsAlertErrorColorVisible
}: InvoceDDTCardProps) => {
  const { t } = useTranslation();
  const unplugDataGridRefreshRef = useRef<(() => void) | null>(null);

  const [isAddDtdDialogOpen, setIsAddDtdDialogOpen] = useState(false);

  const handleCloseAddDtdDialog = () => setIsAddDtdDialogOpen(false);
  const handleOpenAddDtdDialog = () => setIsAddDtdDialogOpen(true);

  const handleAddDtdSubmit = async (values) => {
    if (!invoice?.id) return;
    try {
      await transportDocumentService.update(+values.transport_document, { invoice: +invoice?.id });

      handleCloseAddDtdDialog();

      refreshInvoice();
      dataGridRefreshRef.current?.();
      unplugDataGridRefreshRef.current?.();

      setIsAlertErrorColorVisible(false);
    } catch (error) {
      console.error(error);
      toast.error(t(`${TRANSLATION_PREFIX}.link-error`));
    }
  };

  const handleUnplugDDTfromInvoice = useCallback(
    async (ddtId: string | number) => {
      try {
        await transportDocumentService.update(+ddtId, { invoice: null });

        toast.success(t(`${TRANSLATION_PREFIX}.unplug-success`));

        refreshInvoice();
        dataGridRefreshRef.current?.();
        unplugDataGridRefreshRef.current?.();
      } catch (error) {
        console.error(error);
        toast.error(t(`${TRANSLATION_PREFIX}.unplug-error`));
      }
    },
    [dataGridRefreshRef, refreshInvoice]
  );

  const unplugColumns = TransportDocumentColumns(false, null, handleUnplugDDTfromInvoice)
    .filter((col) => !FIELDS_TO_FILTER_OUT.includes(col.field))
    .map((col) => ({
      ...col,
      filterable: false,
      sortable: false
    }));

  return (
    <>
      <EnhancedCard
        title={t(`${TRANSLATION_PREFIX}.ddt-list`)}
        subtitle={t(`${TRANSLATION_PREFIX}.ddt-subtitle`)}
        buttons={
          <AddButton
            onClick={handleOpenAddDtdDialog}
            prefix={TRANSLATION_PREFIX}
            label="add-transport-document"
          />
        }>
        <EnhancedDataGrid
          columns={unplugColumns}
          service={transportDocumentService}
          refresh={unplugDataGridRefreshRef}
          disableDelete
          outerParams={{
            invoice_ref: invoice?.ref
          }}
        />
      </EnhancedCard>
      <InvoiceAddDDTDialog
        isOpen={isAddDtdDialogOpen}
        onClose={handleCloseAddDtdDialog}
        onSubmit={handleAddDtdSubmit}
        supplierId={Number(invoice?.supplier_id)}
      />
    </>
  );
};

export default InvoceDDTCard;
